import { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { PRACTICE_AREAS, TEAM } from "@/data/content";

const STATIC_LABELS = {
  about: "About",
  services: "Areas of Practice",
  blog: "Blog",
  resources: "Resources",
  contact: "Contact",
  "privacy-policy": "Privacy Policy",
  "terms-of-service": "Terms of Service",
};

const ATTORNEY_LABELS = Object.fromEntries(
  TEAM.map((m) => [m.name.toLowerCase().replace(/ /g, "-"), m.name])
);

const prettify = (s) => s.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

export default function Breadcrumbs() {
  const { pathname } = useLocation();

  const trail = useMemo(() => {
    if (pathname === "/" || pathname.startsWith("/admin")) return null;
    const segs = pathname.split("/").filter(Boolean);
    const crumbs = [{ label: "Home", to: "/" }];
    let acc = "";
    segs.forEach((s, i) => {
      acc += `/${s}`;
      let label =
        STATIC_LABELS[s] ||
        PRACTICE_AREAS.find((a) => a.id === s)?.title ||
        ATTORNEY_LABELS[s] ||
        prettify(s);
      let to = acc;
      if (acc === "/construction") {
        label = "Construction Law";
        to = "/services/construction";
      }
      const last = i === segs.length - 1;
      crumbs.push(last ? { label } : { label, to });
    });
    return crumbs.length > 1 ? crumbs : null;
  }, [pathname]);

  if (!trail) return null;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: trail.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.label,
      ...(c.to ? { item: `https://harrisonlitigationteam.com${c.to}` } : {}),
    })),
  };

  return (
    <nav data-testid="breadcrumbs" aria-label="Breadcrumb" className="border-b border-border bg-card/40">
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <ol className="mx-auto flex max-w-7xl flex-wrap items-center gap-2 px-4 pb-3 pt-[92px] font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground sm:px-8 lg:px-16">
        {trail.map((c, i) => (
          <li key={i} className="flex items-center gap-2">
            {i > 0 && <ChevronRight className="h-3 w-3 text-gold/60" />}
            {c.to ? (
              <Link to={c.to} className="transition-colors duration-300 hover:text-gold">{c.label}</Link>
            ) : (
              <span className="text-gold">{c.label}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}
